
import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PsychologistSchedule, PricingData } from './types';

export const useScheduleDataFetcher = () => {
  const [scheduleBlocks, setScheduleBlocks] = useState<PsychologistSchedule[]>([]);
  const [bookedSlots, setBookedSlots] = useState<string[]>([]);
  const [pricing, setPricing] = useState<PricingData>({ price_30min: 150, price_60min: 280 });
  const [isLoading, setIsLoading] = useState(false);

  const loadPsychologistSchedule = useCallback(async (
    psychologistId: string,
    date: string
  ): Promise<void> => {
    setIsLoading(true);
    
    try {
      console.log('📅 [useScheduleDataFetcher] Carregando dados via API:', { 
        psychologistId, 
        date,
        type: typeof date,
        isISOFormat: /^\d{4}-\d{2}-\d{2}$/.test(date)
      });
      
      // Usar a nova API para obter disponibilidade
      const { data: availabilityData, error } = await supabase.functions.invoke('get-psychologist-availability-api', {
        body: {
          p_psychologist_id: psychologistId,
          p_date: date
        }
      });

      if (error) {
        console.error('❌ [useScheduleDataFetcher] Erro na API:', error);
        throw error;
      }

      console.log('✅ [useScheduleDataFetcher] Dados recebidos da API:', availabilityData);

      // Buscar blocos de horário para gerar os slots
      const { data: scheduleData, error: scheduleError } = await supabase
        .from('psychologist_schedule')
        .select('start_time, end_time')
        .eq('psychologist_id', psychologistId)
        .eq('date', date)
        .eq('is_active', true)
        .order('start_time');

      if (scheduleError) {
        console.error('❌ [useScheduleDataFetcher] Erro na query schedule:', scheduleError);
        throw scheduleError;
      }

      console.log('📊 [useScheduleDataFetcher] Schedule blocks:', scheduleData);
      setScheduleBlocks(scheduleData || []);

      // Calcular slots ocupados (todos os slots menos os disponíveis)
      const allSlots: string[] = [];
      scheduleData?.forEach(block => {
        const start = new Date(`2000-01-01T${block.start_time}`);
        const end = new Date(`2000-01-01T${block.end_time}`);
        
        const current = new Date(start);
        while (current < end) {
          const timeSlot = current.toTimeString().substring(0, 5);
          allSlots.push(timeSlot);
          current.setMinutes(current.getMinutes() + 30);
        }
      });

      const availableSlots = availabilityData?.available_slots || [];
      const bookedTimes = allSlots.filter(slot => !availableSlots.includes(slot));
      
      console.log('🔒 [useScheduleDataFetcher] Booked slots calculados:', bookedTimes);
      setBookedSlots(bookedTimes);

      // Configurar preços
      if (availabilityData?.pricing) {
        setPricing({
          price_30min: availabilityData.pricing[30],
          price_60min: availabilityData.pricing[60]
        });
        console.log('💰 [useScheduleDataFetcher] Preços configurados:', availabilityData.pricing);
      }

    } catch (error) {
      console.error('🔥 [useScheduleDataFetcher] Erro fatal:', error);
      setScheduleBlocks([]);
      setBookedSlots([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    scheduleBlocks,
    bookedSlots,
    pricing,
    isLoading,
    loadPsychologistSchedule
  };
};
